import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import {
    ButtonContainer,
    ButtonContiner,
    ErrorMessage,
    SuccessMessage,
} from 'src/components/FreeBetsForm/FreeBetsForm.style';
import { PaymentAdditionsNew } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/PaymentsAdditionsNew';
import { DropdownContainer } from 'src/components/ManualAdjustmentForm/ManualAdjustmentForm.style';
import { CustomDropdown } from 'src/components/RealityCheckForm/CustomDropdown';
import {
    ManualAdjustmentFormState,
    MANUAL_ADJUSTMET_METHOD_OPTIONS,
    MANUAL_ADJUSTMET_TRADER_OPTIONS,
    MANUAL_ADJUSTMET_TYPE_OPTIONS,
} from 'src/components/ManualAdjustmentForm/ManualAdjustmentForm.state';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';

interface WithdrawFormPropsType {
    customerId: number;
    submitCallback: () => void;
}

export const ManualAdjustmentForm = observer(
    'ManualAdjustmentForm',
    ({ customerId, submitCallback }: WithdrawFormPropsType) => {
        const { sdkStaff, models, permissionGrantsState } = useAppStateContext();
        const common = useCommon();
        const configState = ConfigState.get(common);

        const [stateAutoMap] = useState(
            () =>
                new AutoMap(
                    (customerId: number) =>
                        new ManualAdjustmentFormState(
                            sdkStaff,
                            common.session,
                            permissionGrantsState,
                            models,
                            customerId,
                            submitCallback,
                            configState
                        )
                )
        );
        const state = stateAutoMap.get(customerId);

        return (
            <>
                <Input
                    dataTest='manual-adjustment-amount-input'
                    formInputState={state.amount}
                    onChange={state.onChange}
                    label='Amount'
                    placeholder='Amount'
                />

                <ButtonContainer>
                    <PaymentAdditionsNew
                        value={state.amount.value}
                        currency={state.getCurrency}
                        onChange={state.handleSetAmount}
                        handleActionOnInput={state.handleClear}
                    />
                </ButtonContainer>

                <DropdownContainer>
                    <CustomDropdown
                        optionsList={() => MANUAL_ADJUSTMET_METHOD_OPTIONS}
                        selectedValue={() => state.manualAdjustmentMethodValue}
                        onChangeCallback={state.handleSetManualAdjustmentMethodValue}
                        placeholder='Method'
                        dataTest='manual-adjustment-method-select'
                        labelHeader='Manual Adjustment Method'
                    />
                </DropdownContainer>
                <DropdownContainer>
                    <CustomDropdown
                        optionsList={() => MANUAL_ADJUSTMET_TYPE_OPTIONS}
                        selectedValue={() => state.manualAdjustmentTypeValue}
                        onChangeCallback={state.handleSetManualAdjustmentTypeValue}
                        placeholder='Type'
                        dataTest='manual-adjustment-type-select'
                        labelHeader='Manual Adjustment Type'
                    />
                </DropdownContainer>
                {state.manualAdjustmentTypeValue === 'manual' || state.manualAdjustmentTypeValue === 'casiono-bonus' ? (
                    <DropdownContainer>
                        <CustomDropdown
                            optionsList={() => MANUAL_ADJUSTMET_TRADER_OPTIONS}
                            selectedValue={() => state.manualAdjustmentTraderValue}
                            onChangeCallback={state.handleSetManualAdjustmentTraderValue}
                            placeholder='Trader'
                            dataTest='manual-adjustment-trader-select'
                            labelHeader='Manual Adjustment Trader'
                        />
                    </DropdownContainer>
                ) : null}

                {state.isSubmited === true && state.isSelectsNotFilled === true ? (
                    <ErrorMessage data-test='fill-all-selects-error-message'>Fill all selects</ErrorMessage>
                ) : null}

                <Textarea
                    formState={state.textArea}
                    label='Description'
                    dataTest='manual-adjustment-description'
                />

                {state.successMessage === null ? null : (
                    <SuccessMessage data-test='success-message'>{state.successMessage}</SuccessMessage>
                )}
                {state.errorMessage === null ? null : (
                    <ErrorMessage data-test='error-message'>{state.errorMessage}</ErrorMessage>
                )}
                {state.hasPermission === true ? null : (
                    <ErrorMessage>{`You don't have enough permissions to edit this`}</ErrorMessage>
                )}

                <table className='freebet-adjustment-form__summary'>
                    <tbody>
                        <tr data-test='manual-adjustment-form-current-balance'>
                            <th>Current balance:</th>
                            <td>{state.getBalance.format(state.getCurrency)}</td>
                        </tr>
                        <tr data-test='manual-adjustment-form-withdrawable-balance'>
                            <th>Withdrawable balance:</th>
                            <td>{state.getWithdrawableBalance.format(state.getCurrency)}</td>
                        </tr>
                        {configState.config.failedWithdrawalsFundsEnabled ? (
                            <tr data-test='manual-adjustment-form-failed-withdrawals-funds'>
                                <th>Failed withdrawals funds:</th>
                                <td>{state.getFailedWithdrawalsFunds.format(state.getCurrency)}</td>
                            </tr>
                        ) : null}
                        <tr data-test='manual-adjustment-form-new-balance'>
                            <th>New balance:</th>
                            <td>{state.newBalance.format(state.getCurrency)}</td>
                        </tr>
                    </tbody>
                </table>
                <ButtonContiner>
                    <button
                        data-test='submit-button'
                        className='btn'
                        type='submit'
                        onClick={state.handleSubmit}
                    >
                        Submit
                    </button>
                </ButtonContiner>
            </>
        );
    }
);
